import React, { useEffect, useState } from "react";
import ButtonGroup, { ButtonOption } from "@amzn/meridian/button-group";
import { TableComponent } from "../../../components/table/TableComponent";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import { useTable } from "../../../components/table/hooks";
import { kebabCase } from "lodash";
import { StatusEnum } from "../../../endpoints/getArtifacts";
import Row from "@amzn/meridian/row";
import { Space } from "../../../components/space/Space";
import { tableMapping } from "../../../components/table/definitions";

export interface IMailsToProperty {
    artifactId: string;
    associatedArtifactType: string;
}

const MailsTo: React.FC<IMailsToProperty> = props => {
    const { artifactId, associatedArtifactType } = props;
    const [documentType, setDocumentType] = useState("TAX_BILL");
    const [associatedPropertyType, setAssociatedPropertyType] = useState("REAL_PROPERTY");
    const { currentTable, updateCurrentTable } = useTable();

    useEffect(() => {
        if (documentType.trim()) {
            const artifactTypePrefix =
                associatedPropertyType === "REAL_PROPERTY" ? "real" : "personal";
            const tableKey = kebabCase(`${documentType}-${artifactTypePrefix}`);

            updateCurrentTable(tableKey);
        }
    }, [documentType, associatedPropertyType]);

    const handleDocumentTypeChange = (value: string) => {
        setDocumentType(value);
    };

    const renderButtonOptions = () => {
        const options = [
            <ButtonOption key="tax-bill" value="TAX_BILL">
                Tax bill
            </ButtonOption>,
            <ButtonOption key="hearing-notice" value="HEARING_NOTICE">
                Hearing notices
            </ButtonOption>,
            <ButtonOption key="value-notice" value="VALUE_NOTICE">
                Value notice
            </ButtonOption>,
            <ButtonOption key="check" value="CHECK">
                Check
            </ButtonOption>,
        ];

        if (associatedPropertyType === "PERSONAL_PROPERTY") {
            options.push(
                <ButtonOption key="tax-return" value="TAX_RETURN">
                    Tax return
                </ButtonOption>
            );
        }

        options.push(
            <ButtonOption key="miscellaneous" value="MISCELLANEOUS">
                Miscellaneous
            </ButtonOption>
        );

        return options;
    };

    return (
        <>
            <Row backgroundColor={"subdued"} spacingInset={"300"}>
                <Box>
                    <Text>Category</Text>
                    <Space size={"xxs"} />
                    <ButtonGroup
                        value={associatedPropertyType}
                        onChange={setAssociatedPropertyType}
                    >
                        <ButtonOption key="REAL_PROPERTY" value="REAL_PROPERTY">
                            Real property
                        </ButtonOption>

                        <ButtonOption key="PERSONAL_PROPERTY" value="PERSONAL_PROPERTY">
                            Personal property
                        </ButtonOption>
                    </ButtonGroup>
                </Box>
                <Box>
                    <Text>Mail type</Text>
                    <Space size={"xxs"} />
                    <ButtonGroup value={documentType} onChange={handleDocumentTypeChange}>
                        {renderButtonOptions()}
                    </ButtonGroup>
                </Box>
            </Row>

            <Space size={"s"} />
            <Text type={"h300"}>{tableMapping[currentTable]?.label} to this property</Text>
            <Space size={"xs"} />
            {documentType && (
                <TableComponent
                    tableQueryParams={{
                        associatedArtifactType:
                            associatedArtifactType === "PARCEL" ? "PARCEL" : "REAL_PROPERTY",
                        associatedArtifactId: artifactId,
                        associatedPropertyType: associatedPropertyType,
                        documentType,
                        status: [
                            StatusEnum.CLOSED,
                            StatusEnum.OPEN,
                            StatusEnum.UNREAD,
                            StatusEnum.OVERDUE,
                        ],
                    }}
                    tableUseCase="GET_ASSOCIATED_DOCUMENTS"
                />
            )}
        </>
    );
};

export default MailsTo;
